<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Conference Records" icon="fa-screen-users"/>
            <p class="mt-6 has-text-white" v-if="!registrations"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your records...</p>
            <div v-else class="mt-6 content">
                <div class="columns">
                    <div class="column is-9-fullhd">
                        <empty-state v-if="registrations.length === 0" icon="fa-info-circle" title="You have no conference registrations." text="Browse the Conference Center to get started.">
                            <router-link :to="{name: 'conference-center'}"><b-button type="is-light" outlined size="is-small"><i class="fad fa-presentation mr-2"></i>Browse Conferences</b-button></router-link>
                        </empty-state>
                        <div v-else>
                            <section class="mb-6 pb-2" v-if="recentRegistrations.length">
                                <section-header title="Recently Registered" icon="fa-arrow-right" :count="recentRegistrations.length"/>
                                <registration :registration="r" v-for="r in recentRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="featuredConferences.length">
                                <section-header title="Active & Recent Conferences" icon="fa-calendar-alt" :count="featuredConferences.length"/>
                                <registration :registration="r" v-for="r in featuredConferences" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="alertRegistrations.length">
                                <section-header title="Records Requiring Attention" icon="fa-exclamation-triangle" :count="alertRegistrations.length"/>
                                <registration :registration="r" v-for="r in alertRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2">
                                <section-header title="All Conference Records" icon="fa-screen-users" :count="registrations.length"/>

                                <!-- Year accordions (shared archive pattern with Course
                                     Enrollments / Reports); the current year opens by default -->
                                <archive-section v-for="year in recordYears" :key="'yr-' + year"
                                                 icon="fa-calendar" :title="year" :count="getByYear(year).length"
                                                 :open="year === defaultOpenYear">
                                    <registration :registration="r" v-for="r in getByYear(year)" :key="r.id" class="mb-5"/>
                                </archive-section>
                            </section>
                        </div>
                    </div>
                    <div class="column has-text-white has-text-right is-3-fullhd is-hidden-touch is-hidden-desktop-only is-hidden-widescreen-only">
                        <div class="mb-6">
                            <router-link :to="{name: 'conference-center'}">
                                <b-button type="is-light" outlined><i class="fad fa-browsers mr-3"></i>Browse Available Conferences</b-button>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {client as http} from "../../http_client";
    import Registration from "../../components/Registration";
    import ArchiveSection from "../../components/framework/ArchiveSection";
    import SectionHeader from "../../components/framework/SectionHeader";
    import EmptyState from "../../components/framework/EmptyState";
    import {mapActions, mapGetters} from "vuex";
    import moment from "moment/moment";
    import Conference from "../../components/Conference.vue";

    export default {
        components: {ArchiveSection, SectionHeader, EmptyState, Conference, Registration, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                showCompleted: false
            };
        },
        computed: {
            ...mapGetters(['registrations']),
            // years are sorted descending; open the current year, otherwise
            // the most recent past year, otherwise the oldest section
            defaultOpenYear: function() {
                const years = this.recordYears;
                if (!years.length) return null;
                const currentYear = new Date().getFullYear();
                return years.find(y => y <= currentYear) || years[years.length - 1];
            },
            recentRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let created = moment(r.created_at);
                        return (created.isBetween(moment(), moment().clone().add(30, 'days')) ||
                            created.isBetween(moment().clone().subtract(14, 'days'), moment()));
                    });
                } else {
                    return [];
                }
            },
            alertRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        return r.isIncompleteApps;
                    });
                } else {
                    return [];
                }
            },
            featuredConferences: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let start = moment(r.conferences.start_at);
                        let end = moment(r.conferences.end_at);
                        return ((start.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    start.isBetween(moment().clone().subtract(14, 'days'), moment())) ||
                                (end.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    end.isBetween(moment().clone().subtract(14, 'days'), moment())))
                            && !this.recentRegistrations.includes(r);
                    });
                } else {
                    return [];
                }
            },
            recordYears: function() {
                if (this.registrations) {
                    const years = new Set();
                    this.registrations.forEach(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        for (let y = startYear; y <= endYear; y++) {
                            years.add(y);
                        }
                    });
                    return [...years].sort((a, b) => b - a);
                } else {
                    return [];
                }
            }
        },
        methods: {
            ...mapActions(['getRegistrations']),
            getByYear(year) {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        return year >= startYear && year <= endYear;
                    });
                } else {
                    return [];
                }
            }
        },
        mounted() {
            this.getRegistrations();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }
</style>


